/**
 * Script de détection du pays du visiteur
 * Utilise plusieurs méthodes de géolocalisation avec fallback
 */

class CountryDetector {
    constructor(options = {}) {
        this.options = {
            timeout: options.timeout || 5000,
            fallbackCountry: options.fallbackCountry || 'US',
            useMultipleSources: options.useMultipleSources !== false,
            onSuccess: options.onSuccess || function(country) { console.log('Pays détecté:', country); },
            onError: options.onError || function(error) { console.error('Erreur détection pays:', error); }
        };
        
        this.detectedCountry = null;
        this.detectionMethods = [
            this.detectFromIPAPI,
            this.detectFromIPInfo,
            this.detectFromCloudflare,
            this.detectFromTimezone
        ];
    }

    async detect() {
        // Vérifier le cache localStorage
        try {
            const cached = localStorage.getItem('detectedCountry');
            if (cached) {
                const country = JSON.parse(cached);
                this.detectedCountry = country;
                this.options.onSuccess(country);
                return country;
            }
        } catch (e) {}

        for (const method of this.detectionMethods) {
            try {
                const country = await this.withTimeout(method.bind(this)(), this.options.timeout);
                if (country) {
                    this.detectedCountry = country;
                    try { localStorage.setItem('detectedCountry', JSON.stringify(country)); } catch (e) {}
                    this.options.onSuccess(country);
                    return country;
                }
            } catch (error) {
                console.warn('Méthode de détection échouée:', error.message);
                continue;
            }
        }

        // Fallback sur navigator.language
        let navLang = (navigator.language || navigator.userLanguage || '').slice(0,2).toUpperCase();
        if (navLang) {
            const fallbackCountry = {
                countryCode: navLang,
                countryName: this.getCountryName(navLang),
                source: 'navigator.language'
            };
            this.detectedCountry = fallbackCountry;
            try { localStorage.setItem('detectedCountry', JSON.stringify(fallbackCountry)); } catch (e) {}
            this.options.onSuccess(fallbackCountry);
            return fallbackCountry;
        }

        this.detectedCountry = this.options.fallbackCountry;
        this.options.onError('Toutes les méthodes ont échoué, utilisation du pays par défaut');
        return this.detectedCountry;
    }

    async detectFromIPAPI() {
        const response = await fetch('https://ipapi.co/json/');
        if (!response.ok) throw new Error('Échec ipapi.co');
        
        const data = await response.json();
        return {
            countryCode: data.country_code,
            countryName: data.country_name,
            city: data.city,
            region: data.region,
            source: 'ipapi.co'
        };
    }

    async detectFromIPInfo() {
        const response = await fetch('https://ipinfo.io/json');
        if (!response.ok) throw new Error('Échec ipinfo.io');
        
        const data = await response.json();
        return {
            countryCode: data.country,
            countryName: this.getCountryName(data.country),
            city: data.city,
            region: data.region,
            source: 'ipinfo.io'
        };
    }

    async detectFromCloudflare() {
        const response = await fetch('https://cloudflare.com/cdn-cgi/trace');
        if (!response.ok) throw new Error('Échec Cloudflare');
        
        const text = await response.text();
        const lines = text.split('\n');
        const locLine = lines.find(line => line.startsWith('loc='));
        
        if (!locLine) throw new Error('Pas de données de localisation Cloudflare');
        
        const countryCode = locLine.split('=')[1];
        return {
            countryCode: countryCode,
            countryName: this.getCountryName(countryCode),
            source: 'cloudflare'
        };
    }

    detectFromTimezone() {
        return new Promise((resolve) => {
            try {
                const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                const countryCode = this.timezoneToCountry(timezone);
                
                if (countryCode) {
                    resolve({
                        countryCode: countryCode,
                        countryName: this.getCountryName(countryCode),
                        timezone: timezone,
                        source: 'timezone'
                    });
                } else {
                    resolve(null);
                }
            } catch (error) {
                resolve(null);
            }
        });
    }

    withTimeout(promise, timeout) {
        return Promise.race([
            promise,
            new Promise((_, reject) => 
                setTimeout(() => reject(new Error('Timeout')), timeout)
            )
        ]);
    }

    timezoneToCountry(timezone) {
        const timezoneMap = {
            'Europe/Paris': 'FR',
            'Europe/London': 'GB',
            'Europe/Berlin': 'DE',
            'Europe/Rome': 'IT',
            'Europe/Madrid': 'ES',
            'America/New_York': 'US',
            'America/Los_Angeles': 'US',
            'America/Chicago': 'US',
            'America/Denver': 'US',
            'America/Toronto': 'CA',
            'America/Montreal': 'CA',
            'Asia/Tokyo': 'JP',
            'Asia/Shanghai': 'CN',
            'Asia/Kolkata': 'IN',
            'Australia/Sydney': 'AU',
            'Australia/Melbourne': 'AU'
        };
        
        return timezoneMap[timezone] || null;
    }

    getCountryName(countryCode) {
        const countryNames = {
            'FR': 'France',
            'GB': 'Royaume-Uni',
            'DE': 'Allemagne',
            'IT': 'Italie',
            'ES': 'Espagne',
            'US': 'États-Unis',
            'CA': 'Canada',
            'JP': 'Japon',
            'CN': 'Chine',
            'IN': 'Inde',
            'AU': 'Australie',
            'BR': 'Brésil',
            'MX': 'Mexique',
            'RU': 'Russie',
            'BE': 'Belgique',
            'NL': 'Pays-Bas',
            'CH': 'Suisse'
        };
        
        return countryNames[countryCode] || countryCode;
    }

    getCountryInfo() {
        return this.detectedCountry;
    }
}

// Fonction utilitaire simple pour utilisation directe
async function detectVisitorCountry(callback) {
    const detector = new CountryDetector({
        onSuccess: callback,
        onError: (error) => {
            console.error('Erreur de détection:', error);
            if (callback) callback(null, error);
        }
    });
    
    return await detector.detect();
}

// Auto-détection au chargement de la page (optionnel)
if (typeof window !== 'undefined' && window.addEventListener) {
    window.addEventListener('load', function() {
        // Décommenter la ligne suivante pour auto-détection
        // detectVisitorCountry((country) => console.log('Pays auto-détecté:', country));
    });
}

// Export pour utilisation en module (si supporté)
if (typeof module !== 'undefined' && module.exports) {
    module.exports = { CountryDetector, detectVisitorCountry };
}